import { Flex, IconButton, Text } from '@chakra-ui/react';
import { FC } from 'react';

import UpvoteIconActive from '@/assets/icons/ic-thumb-up-active.svg';
import UpvoteIcon from '@/assets/icons/ic-thumb-up.svg';

import { ReactionType } from '@/enums/ReactionType';
import { createCommentReaction, destroyCommentReaction, queryCommentReactions } from '@/fetchers/reactions';
import useSession from '@/hooks/useSession';
import { useStores } from '@/hooks/useStores';
import { useMutation, useResources } from '@/lib/datx-jsonapi-react';
import { cloneResponse } from '@/lib/datx-jsonapi-react/utils';
import { Comment } from '@/resources/Comment';
import { Reaction } from '@/resources/Reaction';

export interface ICommentUpVoteProps {
  comment?: Comment;
}

export const CommentUpVote: FC<ICommentUpVoteProps> = ({ comment }) => {
  const { user } = useSession();
  const { data: reactions, mutate } = useResources(queryCommentReactions(comment.id, user?.id));
  const [createReaction, { status: createStatus }] = useMutation(createCommentReaction, {
    onSuccess: async ({ data }) => {
      await mutate((currentReactions) => {
        // this is unsafe side-effect mutation but it works
        comment.metadata.thumbsUpCount++;
        return cloneResponse<Reaction>(currentReactions, [data.data as Reaction]);
      }, false);
    },
  });
  const [destroyReaction, { status: destroyStatus }] = useMutation(destroyCommentReaction, {
    onSuccess: async () => {
      await mutate((currentReactions) => {
        // this is unsafe side-effect mutation but it works
        comment.metadata.thumbsUpCount--;
        return cloneResponse<Reaction>(currentReactions, []);
      }, false);
    },
  });

  const isDisabled = !user;
  const isActive = reactions?.length > 0;
  const isLoading = createStatus === 'running' || destroyStatus === 'running';

  const onToggle = () => {
    if (isActive) {
      destroyReaction({ reactionId: reactions?.[0]?.id });
    } else {
      createReaction({ type: ReactionType.ThumbsUp, commentId: comment.id });
    }
  };

  return (
    <Flex alignItems="center">
      <IconButton
        variant="ghost"
        aria-label="upvotes"
        icon={isActive ? <UpvoteIconActive /> : <UpvoteIcon />}
        mr={1}
        minW="0"
        height="auto"
        isActive={isActive}
        isDisabled={isDisabled}
        isRound
        isLoading={isLoading}
        onClick={onToggle}
        sx={{
          svg: {
            width: '24px',
            height: '24px',
          },
        }}
      />
      <Text as="span" fontSize="sm">
        {comment.metadata.thumbsUpCount}
      </Text>
    </Flex>
  );
};
